import React, { useState, useEffect, useCallback } from 'react';
import { authenticatedFetch } from '../utils/apiUtils';
import './VocabularyScreen.css';

export interface Vocable {
  id: string;
  japanese: string;
  kana: string;
  english: string[];
  partOfSpeech?: string;
  verbType?: string;
  transitive?: boolean;
  conjugations?: Record<string, string>;
  username?: string;
}

interface VocabularyScreenProps {
  onBack: () => void;
  onAddVocable: () => void;
  onViewVocable: (vocable: Vocable) => void;
  refreshToken: number;
}

function VocabularyScreen({ onBack, onAddVocable, onViewVocable, refreshToken }: VocabularyScreenProps) {
  const [vocables, setVocables] = useState<Vocable[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');

  const loadVocables = useCallback(async () => {
    setIsLoading(true);
    setError('');
    try {
      const response = await authenticatedFetch('/api/vocabulary/vocables');

      if (!response.ok) {
        throw new Error('Failed to fetch vocabulary');
      }

      const data = await response.json();
      setVocables(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load vocabulary');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadVocables();
  }, [loadVocables, refreshToken]);

  const lowerSearch = searchTerm.toLowerCase();
  const filteredVocables = vocables.filter((v) => {
    const matchesJapanese = v.japanese?.includes(searchTerm);
    const matchesKana = v.kana?.includes(searchTerm);
    const matchesEnglish = v.english?.some((meaning) =>
      meaning.toLowerCase().includes(lowerSearch)
    );
    const matchesConjugations = v.conjugations ? 
      Object.values(v.conjugations).some((conjugation) =>
        conjugation?.toLowerCase().includes(lowerSearch)
      ) : false;
    return Boolean(matchesJapanese || matchesKana || matchesEnglish || matchesConjugations);
  });

  const handleAddClick = () => {
    onAddVocable();
  };

  const handleVocableClick = (vocable: Vocable) => {
    onViewVocable(vocable);
  };

  if (isLoading) {
    return (
      <div className="vocabulary-screen">
        <div className="vocabulary-container">
          <div className="loading-state">Loading vocabulary...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="vocabulary-screen">
      <div className="vocabulary-container">
        <div className="vocabulary-header">
          <h2 className="vocabulary-title">Vocabulary</h2>
          <button className="back-button" onClick={onBack}>
            ← Back
          </button>
        </div>

        {error && (
          <div className="error-message">
            {error}
          </div>
        )}

        <div className="vocabulary-toolbar">
          <input
            type="text"
            className="search-input"
            placeholder="Search vocabulary..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          <button className="btn btn-primary" onClick={handleAddClick}>
            Add
          </button>
        </div>

        <div className="vocabulary-list">
          {filteredVocables.length === 0 ? (
            <div className="empty-state">
              {searchTerm ? 'No vocabulary found matching your search.' : 'No vocabulary yet. Start learning!'}
            </div>
          ) : (
            filteredVocables.map((vocable) => (
              <button
                key={vocable.id}
                type="button"
                className="vocable-card"
                onClick={() => handleVocableClick(vocable)}
              >
                <div className="vocable-japanese">{vocable.japanese}</div>
                {vocable.kana !== vocable.japanese && (
                  <div className="vocable-kana">{vocable.kana}</div>
                )}
                <div className="vocable-english">
                  {vocable.english.join(', ')}
                </div>
              </button>
            ))
          )}
        </div>
      </div>
    </div>
  );
}

export default VocabularyScreen;

